import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import styles from './TaskStatusPicker.module.sass'
import { Task } from '../../../../../../responses/tasks'
import Button, { ButtonStyle } from '../../../../../controls/Button/Button'
import ShadowContainer from '../../../../../etc/popover/ShadowContainer/ShadowContainer'
import { TaskStatus } from '../../../../../../constants/enums'
import { CommentData } from '../../../../../../responses/comments'
import OkCancel from '../../../../../controls/OkCancel/OkCancel'
import DesignStatusPicker from '../../../../../etc/DesignStatusPicker/DesignStatusPicker'
import { useBoolean, useRootSelector } from '../../../../../../utils/hooks'
import { RequestStatus } from '../../../../../../responses/response'
import Popover from '../../../../../etc/Popover'
import TaskOpenIcon from '../../../../../../../files/task-open.svg'
import TaskCloseIcon from '../../../../../../../files/task-closed.svg'
import { errorToast } from '../../../../../controls/Toaster/Toaster'
import { xor } from '../../../../../../utils/utils'
import QuillEditor from '../../../../../etc/Editor/QuillEditor'
import { Colors } from '../../../../../../styles'
import { useQueries } from '../../../../../../utils/hooks/useQueries'

interface Props {
    className?: string,
    task: Task,
    disabled?: boolean,
    inactive?: boolean,
    onChange?: (task: Task, comment: CommentData) => void,
}

const TaskStatusPicker: FunctionComponent<Props> = props => {
    const [open, setOpen] = useState(false)
    const {state: hover, setTrue: onEnter, setFalse: onLeave} = useBoolean(false)
    const [task, setTask] = useState(props.task)
    const [text, setText] = useState('')
    const waitUpdate = useRef(false)
    const fetching = useRootSelector(state => state.data.task.updateStatus === RequestStatus.FETCHING)

    const opened = task.status_id === TaskStatus.OPENED
    const actionWord = task.status_id === TaskStatus.CLOSED ? 'Открыть' : 'Закрыть'
    const incomplete = false//isTaskIncomplete(task)

    const {isMobile} = useQueries()

    const clear = useCallback(() => {
        waitUpdate.current = false
        setTask(props.task)
        setOpen(false)
        setText('')
    }, [props.task])

    useEffect(() => {
        !open && clear()
    }, [clear, open])

    useEffect(() => {
        (waitUpdate.current || !open) && clear()
    }, [clear, open, props.task])

    return <Popover
        isOpen={props.disabled ? false : open}
        onClickOutside={() => setOpen(!open)}
        content={<ShadowContainer className={styles.picker}>
            <div className={styles.header}>
                {
                    incomplete && <div className={styles.subheader}>Проставьте статус и исполнителя</div>
                }
                <div className={styles.title}>{actionWord} задачу</div>
            </div>
            <div>
                {
                    incomplete &&
                    <DesignStatusPicker type={'tender'} task={task} onChange={setTask}/>
                }
            </div>

            <QuillEditor
                className={styles.editor}
                placeholder={'Добавить комментарий'}
                value={text}
                onChange={setText}
            />

            <div className={styles.actions}>
                <OkCancel
                    disabled={incomplete || fetching}
                    text={actionWord}
                    onOk={() => {
                        waitUpdate.current = true
                        props.onChange?.(
                            {
                                ...task,
                                status_id: task.status_id === TaskStatus.CLOSED ? TaskStatus.OPENED : TaskStatus.CLOSED,
                            },
                            {text}
                        )
                    }}
                    onCancel={() => setOpen(false)}
                />
            </div>
        </ShadowContainer>}
    >
        <Button
            className={props.className}
            alwaysVisible
            active={open}
            inactive={props.inactive}
            disabled={props.disabled}
            buttonStyle={ButtonStyle.BORDERED}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            onDisabledClick={() => errorToast(isMobile?
                'Изменять статус задачи можно только в десктопной версии':
                'В архивных проектах изменение статуса недоступно'
            )}
            color={opened ? Colors.blue : Colors.green}
            onClick={() => setOpen(!open)}
        >
            {xor(open, hover) ? <TaskCloseIcon/>: <TaskOpenIcon/>}
            {
                opened ?
                    (hover ?
                        'Закрыть' :
                        'Открыта') :
                    (hover ?
                        'Открыть' :
                        'Закрыта')
            }
        </Button>
    </Popover>
}

export default TaskStatusPicker